import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  LueftungGrundlagen, LueftungRaumauslass, LueftungRaumschallliste, LueftungRaumschallRaum, LueftungRaumschallzeile,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe,
  LoadingState, StatusBadge, Vorbehalte, formatiereZahl, spalteFuer, useFilterInUrl, type FilterDefinition,
  type Spalte,
} from '../../muster';
import { rechenstandText } from './pruefung';
import { RaumakustikAbschnitt } from './SchallAnnahmen';
import { raumVorbehalte, spektrum, ueberschreitungText } from './schallregeln';
import { Berichtsvorbehaltkasten, useBerichtsvorbehalte } from './LueftungBerichtsvorbehalte';
import './schall.css';

/** Eine Zeile eines Spektrums: das Oktavband und je Reihe ein Pegel. */
type Bandzeile = ReturnType<typeof spektrum>[number];

/**
 * Höchstzahl der Räume je Abruf - die Grenze des Servers. Die Seite filtert
 * Ebene und „über Ziel“ selbst; die Kennzahlen zählen immer alle Räume.
 * Vorher holte sie 1.000 und filterte am Server, sodass die Kennzahlen mit dem
 * Filter wechselten.
 */
export const RAUMGRENZE = 5000;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SCHALLDRUCKPEGEL JE RAUM (TV6b, TX-STANDARD 1.6 S1) - Befund L-02 vom 15.09.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Schallseite zeigt die Kette je STRANG; diese Seite, was im Raum ankommt:
 * die energetische Summe aller Auslässe des Raums, zuzüglich Diffusfeld über
 * die äquivalente Absorptionsfläche (Sabine). Ein Raum hat selten einen
 * Auslass - die Summe hebt den Pegel im Mittel um 4,2 dB, im schlimmsten Fall
 * um 28,2 dB; solange nur Einzelauslässe dastanden, hielten 111 Räume ihr Ziel
 * scheinbar ein und taten es nicht.
 *
 * Seit TV6b über die Muster: Kopf mit Rechenstand und Exportleiste (PDF
 * LUE-RSC, mit Ebenenfilter), Vorbehalte statt vier Absätzen, höchstens sechs
 * Kennzahlen (vorher zehn), Filter in der Adresse, der Raum im Schubfach mit
 * eigenen Vorbehalten, Spektren als `DataTable`, Zahlen über `formatiereZahl`,
 * Abschnitt „Annahmen und Vorgaben“ mit Nachhallzeit und Zielpegel.
 */
export function Raumschall() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [liste, setListe] = useState<LueftungRaumschallliste | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [grundlagen, setGrundlagen] = useState<LueftungGrundlagen | null>(null);
  const [grundlagenFehler, setGrundlagenFehler] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);

  const laden = useCallback(() => {
    setFehler(null);
    api.lueftungRaumschall(projekt.id, { grenze: RAUMGRENZE }).then(setListe).catch(setFehler);
  }, [api, projekt.id]);

  useEffect(() => {
    laden();
    setGrundlagenFehler(false);
    api.lueftungGrundlagen(projekt.id).then(setGrundlagen).catch(() => { setGrundlagen(null); setGrundlagenFehler(true); });
  }, [api, projekt.id, laden]);

  const filter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'ebene', beschriftung: 'Ebene', art: 'auswahl', platzhalter: 'alle Ebenen',
      optionen: (liste?.jeEbene ?? [])
        .filter((e): e is typeof e & { ebene: string } => !!e.ebene)
        .sort((a, b) => a.ebene.localeCompare(b.ebene, 'de', { numeric: true }))
        .map((e) => ({
          wert: e.ebene,
          beschriftung: `${e.ebene} · ${formatiereZahl(e.anzahl, 0)} Räume`
            + (e.ueberZiel > 0 ? ` · ${formatiereZahl(e.ueberZiel, 0)} über Ziel` : ''),
        })),
    },
    { schluessel: 'ueberziel', beschriftung: 'nur Räume über Zielpegel', art: 'haken' },
  ], [liste]);
  const [werte, setWerte] = useFilterInUrl(filter);
  const ebene = typeof werte.ebene === 'string' ? werte.ebene : '';
  // TV7a-2 (§22 c): der Vorbehaltskasten des Berichts LUE-RSC im Umfang des PDF (alle Ebenen oder die gewählte).
  const berichtsvorbehalte = useBerichtsvorbehalte(api, projekt.id, 'raumschall', { ebene: ebene || undefined });
  const nurUeberZiel = werte.ueberziel === true;

  const zeilen = useMemo(() => (liste?.raeume ?? []).filter((r) => (!ebene || r.ebene === ebene)
    && (!nurUeberZiel || (r.ueberschreitung ?? 0) > 0)), [liste, ebene, nurUeberZiel]);

  const spalten = useMemo<Spalte<LueftungRaumschallzeile>[]>(() => [
    { schluessel: 'raumnummer', titel: 'Raum', immerSichtbar: true, wert: (r) => r.raumnummer },
    { schluessel: 'raumname', titel: 'Bezeichnung', wert: (r) => r.raumname ?? '' },
    { schluessel: 'ebene', titel: 'Ebene', wert: (r) => r.ebene ?? '' },
    { schluessel: 'nutzung', titel: 'Nutzung', wert: (r) => r.nutzungsart ?? '' },
    { schluessel: 'auslaesse', titel: 'Auslässe', art: 'zahl', nachkomma: 0, wert: (r) => r.anzahlAuslaesse },
    { schluessel: 'wege', titel: 'Wege', art: 'zahl', nachkomma: 0, wert: (r) => r.wegeGesamt },
    {
      // Der Direktanteil, der wirklich eingeht: der des maßgebenden Auslasses
      // (PB32, S-01). Die Summe aller steht als obere Schranke im Raum.
      schluessel: 'direkt', titel: 'Direkt (maßg. Auslass)', ...spalteFuer('schallpegel_dba'),
      wert: (r) => r.lpaDirektMassgebendDb,
    },
    { schluessel: 'diffus', titel: 'Diffusfeld', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (r) => r.diffusZuschlagDb },
    {
      // Kein Raumpegel ist KEINE Null. „nicht berechenbar“ steht vor „kein
      // Raumpegel“: Beides ist eine Lücke, nur die eine löst sich über ein
      // Raumdatum (PB45).
      schluessel: 'raumpegel', titel: 'L_p Raum', ...spalteFuer('schallpegel_dba'), wert: (r) => r.lpaRaumDb,
      inhalt: (r) => (r.nichtBerechenbar
        ? <StatusBadge zustand="warnung" text="nicht berechenbar" klein />
        : r.lpaRaumDb === null
          ? <StatusBadge zustand="warnung" text="kein Raumpegel" klein />
          : (r.ueberschreitung ?? 0) > 0
            ? <StatusBadge zustand="fehler" text={`${formatiereZahl(r.lpaRaumDb, 1)} über Ziel`} klein />
            : formatiereZahl(r.lpaRaumDb, 1)),
    },
    { schluessel: 'ziel', titel: 'Zielpegel', art: 'zahl', einheit: 'dB(A)', nachkomma: 0, wert: (r) => r.zielpegelDbA },
    {
      schluessel: 'ueberziel', titel: 'über Ziel', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (r) => r.ueberschreitung,
      inhalt: (r) => ((r.ueberschreitung ?? 0) > 0
        ? (
          <StatusBadge
            zustand={(r.ueberschreitung ?? 0) >= 5 ? 'fehler' : 'warnung'}
            text={ueberschreitungText(r.ueberschreitung ?? 0)}
            klein
          />
        )
        : '—'),
    },
    {
      schluessel: 'absorption', titel: 'Absorptionsfläche', art: 'zahl', einheit: 'm²', nachkomma: 1,
      wert: (r) => r.absorptionsflaecheM2,
    },
    {
      schluessel: 'absorptionHerkunft', titel: 'Herkunft Absorption', art: 'status', wert: (r) => r.absorptionHerkunft,
      inhalt: (r) => (r.absorptionHerkunft === 'fehlt'
        ? <StatusBadge zustand="warnung" text="fehlt" klein />
        : <StatusBadge zustand="neutral" text={r.absorptionHerkunft} klein />),
    },
    { schluessel: 'lautester', titel: 'lautester Auslass', ...spalteFuer('schallpegel_dba'), wert: (r) => r.lautesterAuslassDb },
    {
      // Sind es ALLE Auslässe, gibt es keinen Raumpegel; sind es einige, ist er zu klein.
      schluessel: 'ohneKennwert', titel: 'Auslässe ohne Kennwert', art: 'zahl', nachkomma: 0,
      wert: (r) => r.auslaesseOhneEigengeraeusch,
      inhalt: (r) => (r.auslaesseOhneEigengeraeusch === 0
        ? '—'
        : (
          <StatusBadge
            zustand="warnung"
            text={`${formatiereZahl(r.auslaesseOhneEigengeraeusch, 0)} von ${formatiereZahl(r.anzahlAuslaesse, 0)}`}
            klein
          />
        )),
    },
  ], []);

  const s = liste?.summe;
  const ohneLauf = !!liste && (!s || s.gesamt === 0);

  return (
    <>
      <PageHeader
        titel="Raumpegel Lüftung"
        beschreibung="Schalldruckpegel je Raum: energetische Summe aller Auslässe des Raums je Oktavband, zuzüglich Diffusfeld nach VDI 2081 über die äquivalente Absorptionsfläche (Sabine)."
        meta={(
          <span data-rechenstand>
            {rechenstandText(liste?.stand
              ? { lauf: 'Kanalnetzlauf', werkzeug: 'druckverlust-lueftung', gerechnetAm: liste.stand, modellstand: null, datenstand: '' }
              : null)}
          </span>
        )}
        aktionen={(
          <>
            <ExportLeiste ziele={[{
              art: 'pdf', beschriftung: 'PDF LUE-RSC',
              laden: () => api.lueftungRaumschallberichtPdf(projekt.id, ebene || undefined),
              hinweis: ebene ? `nur Ebene ${ebene}` : undefined,
              gesperrt: !liste || ohneLauf ? { grund: 'Noch keine Raumschallrechnung.' } : undefined,
            }]}
            />
            <Link to={`/projekte/${projekt.id}/lueftung/schall`} className="m-knopf">Schallberechnung je Strang</Link>
          </>
        )}
      />

      {!liste && fehler === null && <LoadingState text="Raumpegel werden abgerufen …" zeilen={10} spalten={8} />}
      {fehler !== null && (
        <ErrorState meldung="Die Raumschallrechnung konnte nicht abgerufen werden." technisch={fehler}
                    beiErneutVersuchen={laden} />
      )}

      {/* Eine Antwort OHNE `summe` ist ein leerer Stand und kein Ladezustand (U6b). */}
      {ohneLauf && (
        <EmptyState
          titel="Für dieses Modell wurde noch keine Raumschallrechnung durchgeführt."
          erklaerung="Sie entsteht im Kanalnetzlauf. Wer sie braucht, löst sie auf der Seite Druckverlust oder Schallberechnung aus - beide rechnen denselben Lauf."
          aktion={<Link to={`/projekte/${projekt.id}/lueftung/schall`} className="m-knopf m-knopf-haupt">Zur Schallberechnung</Link>}
        />
      )}

      {liste && s && !ohneLauf && (
        <>
          <Berichtsvorbehaltkasten stand={berichtsvorbehalte} bericht="raumschall" />

          <KpiReihe>
            <KpiCard titel="Räume mit Auslass" wert={s.gesamt} vergleich={`${formatiereZahl(s.auslaesse, 0)} Auslässe`} />
            <KpiCard
              titel="über Zielpegel" wert={s.ueberZiel}
              zustand={s.ueberZiel > 0 ? 'fehler' : 'gut'}
              zustandText={s.ueberZiel > 0 ? 'Befund raumpegel_ziel' : 'kein Raum über Ziel'}
            />
            <KpiCard titel="größte Überschreitung" wert={s.groessteUeberschreitung} einheit="dB" nachkomma={1} />
            <KpiCard titel="höchster Raumpegel" wert={s.hoechster} einheit="dB(A)" nachkomma={1} />
            <KpiCard titel="nicht berechenbar" wert={s.nichtBerechenbar} vergleich="kein Auslass mit Kennwert" />
            <KpiCard
              titel="Pegel zu klein" wert={s.pegelZuKlein}
              vergleich={`untere Schranke, ${formatiereZahl(s.ohneJedenKennwert, 0)} ohne jeden Kennwert`}
            />
          </KpiReihe>

          <ContentSection
            titel="Raumpegel je Raum"
            beschreibung="Die schlimmsten zuerst: nach der Überschreitung, dann nach dem Pegel. Eine Zeile wählen zeigt den Raum mit seinen Auslässen."
            werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={2} />}
          >
            <DataTable<LueftungRaumschallzeile>
              name="Raumpegel je Raum"
              spalten={spalten}
              zeilen={zeilen}
              schluessel={(r) => r.raumnummer}
              suchePlatzhalter="Raum, Bezeichnung oder Nutzung"
              trefferText={liste.gekuerzt
                ? `${formatiereZahl((liste.raeume ?? []).length, 0)} von ${formatiereZahl(s.gesamt, 0)} Räumen abgerufen - die Kennzahlen zählen alle`
                : undefined}
              ausfuhr
              ausfuhrDateiname="raumschall.csv"
              dicht
              beiZeileKlick={(r) => setGewaehlt(r.raumnummer)}
              aktiveZeile={gewaehlt ?? undefined}
              leer={(
                <EmptyState
                  klein
                  titel={nurUeberZiel || ebene ? 'Kein Raum passt zu diesem Filter.' : 'Kein Raum trägt einen Auslass.'}
                  erklaerung={nurUeberZiel
                    ? 'Kein Raum der gewählten Ebene überschreitet seinen Zielpegel.'
                    : 'Ohne Auslass im Raum gibt es keinen Schalleintrag zu rechnen.'}
                />
              )}
            />
          </ContentSection>

          <ContentSection titel="Wie der Raumpegel entsteht">
            <p className="lue-schall-text">
              Gezählt werden alle Auslässe eines Raums, auch die anderer Anlagen. Der Zielpegel kommt über die
              Nutzungsart aus der Raumakustik (Abschnitt „Annahmen und Vorgaben“). Eine Überschreitung ist ein Befund
              und steht in den Befunden des Laufs (Regel raumpegel_ziel); ab 5 dB Überschreitung ist sie ein Fehler,
              darunter eine Warnung.
            </p>
            <p className="lue-schall-text">
              Die Nachhallzeit, aus der die Absorptionsfläche folgt, ist eine Annahme je Nutzungsart. Wo sie oder das
              Raumvolumen fehlt, bleibt der Raumpegel leer und es steht nur der Direktschall da - das ist keine
              Einhaltung, sondern eine Lücke. Ein Ersatzwert für fehlende Auslasskennwerte wird nicht angesetzt; eine
              obere Schranke gibt es nur dort, wo das Herstellerblatt eine Aussage macht (Festlegung F51).
            </p>
          </ContentSection>

          <RaumakustikAbschnitt
            daten={grundlagen?.raumakustik ?? (grundlagenFehler ? [] : null)}
            fehler={grundlagenFehler}
          />

          {gewaehlt && (
            <Raumdetail key={gewaehlt} projektId={projekt.id} raumnummer={gewaehlt} schliessen={() => setGewaehlt(null)} />
          )}
        </>
      )}
    </>
  );
}

// ═══ Der gewählte Raum (Schubfach) ═══════════════════════════════════════════
//
// Die Frage nach einem zu hohen Raumpegel ist immer dieselbe: welcher Auslass,
// welche Anlage, und hilft dort ein Schalldämpfer. Steht in „maßgeblich“
// „Auslass“, hilft kein Dämpfer im Kanal, sondern nur ein anderer Durchlass
// oder weniger Luft je Auslass.

const AUSLASSSPALTEN: Spalte<LueftungRaumauslass>[] = [
  { schluessel: 'anlage', titel: 'Anlage', immerSichtbar: true, wert: (a) => a.anlage ?? '' },
  { schluessel: 'weg', titel: 'Weg', wert: (a) => a.weg ?? '' },
  {
    schluessel: 'strang', titel: 'Strang', art: 'zahl', nachkomma: 0, wert: (a) => a.nummer,
    inhalt: (a) => (a.istMassgebend
      ? <>{formatiereZahl(a.nummer, 0)} <StatusBadge zustand="info" text="maßgebend" klein /></>
      : formatiereZahl(a.nummer, 0)),
  },
  { schluessel: 'auslass', titel: 'Auslass', art: 'zahl', nachkomma: 0, wert: (a) => a.auslassId },
  { schluessel: 'bauform', titel: 'Bauform', wert: (a) => a.auslassBauform ?? '' },
  { schluessel: 'volumenstrom', titel: 'Volumenstrom', ...spalteFuer('volumenstrom_luft'), wert: (a) => a.volumenstromM3h },
  { schluessel: 'druckverlust', titel: 'Druckverlust', art: 'zahl', einheit: 'Pa', nachkomma: 1, wert: (a) => a.druckverlustPa },
  { schluessel: 'lwa', titel: 'L_WA', ...spalteFuer('schallpegel_dba'), wert: (a) => a.lwaAuslassDb },
  { schluessel: 'daempfung', titel: 'Dämpfung', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (a) => a.daempfungDb },
  {
    schluessel: 'lpa', titel: 'L_pA direkt', ...spalteFuer('schallpegel_dba'), wert: (a) => a.lpaDirektDb,
    inhalt: (a) => (a.istLautester
      ? <>{formatiereZahl(a.lpaDirektDb, 1)} <StatusBadge zustand="info" text="lautester" klein /></>
      : formatiereZahl(a.lpaDirektDb, 1)),
  },
  { schluessel: 'massgeblich', titel: 'maßgeblich', wert: (a) => a.massgeblich ?? '' },
];

function Raumdetail({ projektId, raumnummer, schliessen }: {
  projektId: string; raumnummer: string; schliessen: () => void;
}) {
  const { api } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<LueftungRaumschallRaum | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  const laden = useCallback(() => {
    setFehler(null);
    api.lueftungRaumschallRaum(projektId, raumnummer).then(setDaten).catch(setFehler);
  }, [api, projektId, raumnummer]);

  useEffect(() => { laden(); }, [laden]);

  const r = daten?.raum;
  const ueber = (r?.ueberschreitung ?? 0) > 0;

  return (
    <Drawer
      offen
      titel={r ? `${r.raumnummer}${r.raumname ? ` · ${r.raumname}` : ''}` : `Raum ${raumnummer}`}
      beschreibung={r?.hinweis ?? undefined}
      breite="720px"
      beiSchliessen={schliessen}
      fuss={<button type="button" className="m-knopf" onClick={schliessen}>schließen</button>}
    >
      {fehler !== null && (
        <ErrorState meldung="Der Raum konnte nicht abgerufen werden." technisch={fehler} beiErneutVersuchen={laden} />
      )}
      {!daten && fehler === null && <LoadingState text="Raum wird abgerufen …" zeilen={4} spalten={6} />}
      {daten && r && (
        <div className="lue-schall-detail">
          <Vorbehalte eintraege={raumVorbehalte(r)} />

          <KpiReihe>
            <KpiCard
              titel="Pegel im Raum" wert={r.nichtBerechenbar ? null : r.lpaRaumDb} einheit="dB(A)" nachkomma={1}
              zustand={r.nichtBerechenbar || r.lpaRaumDb === null || r.zielpegelDbA === null ? undefined : ueber ? 'fehler' : 'gut'}
              zustandText={r.nichtBerechenbar || r.lpaRaumDb === null || r.zielpegelDbA === null ? undefined
                : ueber ? `${formatiereZahl(r.ueberschreitung, 1)} dB über Ziel` : 'unter Zielpegel'}
              vergleich={r.nichtBerechenbar ? 'nicht berechenbar' : undefined}
            />
            <KpiCard titel="Zielpegel" wert={r.zielpegelDbA} einheit="dB(A)" vergleich={r.nutzungsart ?? 'ohne Nutzungsart'} />
            <KpiCard
              titel="Direktschall (maßg. Auslass)" wert={r.lpaDirektMassgebendDb} einheit="dB(A)" nachkomma={1}
              vergleich={r.lpaDirektSummeDb === null ? undefined
                : `Summe aller: ${formatiereZahl(r.lpaDirektSummeDb, 1)} dB(A) - obere Schranke`}
            />
            <KpiCard titel="Diffusfeld" wert={r.diffusZuschlagDb} einheit="dB" nachkomma={1} />
            <KpiCard titel="Absorptionsfläche" wert={r.absorptionsflaecheM2} einheit="m²" nachkomma={1} vergleich={r.absorptionHerkunft} />
            <KpiCard titel="Auslässe" wert={r.anzahlAuslaesse} vergleich={`${formatiereZahl(r.wegeGesamt, 0)} Wege`} />
          </KpiReihe>

          {r.massnahme && (
            <p className="lue-schall-text">
              Erforderliche Zusatzdämpfung (Festlegung F36 vom 20.09.2026): {r.massnahme}
            </p>
          )}
          {r.aussenterminalsAusgelassen > 0 && (
            <p className="lue-schall-text">
              {formatiereZahl(r.aussenterminalsAusgelassen, 0)} Außenluft-/Fortluftterminal(e) liegen lagemäßig in
              diesem Raum und gehen nicht in den Raumpegel ein - sie blasen nicht in den Raum. Ihr Pegel steht an der
              Teilstrecke.
            </p>
          )}

          <DataTable<Bandzeile>
            name="Schalldruckpegel je Oktavband"
            beschreibung="Direktschall: Summe aller Auslässe des Raums; im Raum: mit Diffusfeld."
            spalten={[
              { schluessel: 'band', titel: 'Oktavband', art: 'zahl', einheit: 'Hz', nachkomma: 0, wert: (z) => z.band },
              { schluessel: 'direkt', titel: 'Direktschall, Summe', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (z) => z.direkt },
              { schluessel: 'raum', titel: 'im Raum', art: 'zahl', einheit: 'dB', nachkomma: 1, wert: (z) => z.raum },
            ]}
            zeilen={spektrum({ direkt: r.oktaven, raum: r.oktavenRaum })}
            schluessel={(z) => String(z.band)}
            suche={false}
            dicht
          />

          <DataTable<LueftungRaumauslass>
            name="Auslässe dieses Raums"
            trefferText={daten.gekuerzt
              ? `die lautesten ${formatiereZahl(daten.auslaesse.length, 0)} von ${formatiereZahl(daten.auslaesseGesamt, 0)} Strängen - die Kennzahlen zählen alle`
              : undefined}
            spalten={AUSLASSSPALTEN}
            zeilen={daten.auslaesse}
            schluessel={(a) => a.teilstreckeId}
            ausfuhr
            ausfuhrDateiname={`raumschall-${r.raumnummer}.csv`}
            dicht
            leer={(
              <EmptyState
                klein
                titel="Zu diesem Raum ist keine Teilstrecke auffindbar."
                erklaerung="Der Pegel steht trotzdem - dann trägt der Auslass im Kanalnetz eine andere Raumnummer als die Rechnung."
              />
            )}
          />

          {daten.schallQuelle === 'keine' && (
            <p className="lue-schall-text">
              Für die Anlagen dieses Raums liegt keine abgelegte Schallkette vor - die Pegel je Auslass bleiben
              deshalb leer. Der Raumpegel oben ist davon unberührt.
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
}
